import { useEffect, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PagePromoPopupProps {
  storageKey: string;
  icon?: ReactNode;
  badge?: string;
  title: string;
  description: ReactNode;
  ctaLabel: string;
  onCta: () => void;
  secondaryLabel?: string;
  delayMs?: number;
  accent?: 'primary' | 'amber';
}

/**
 * Page-scoped promo popup. Stores a per-key dismissal flag in sessionStorage so
 * different pages can each show their own popup without ever overlapping.
 */
export function PagePromoPopup({
  storageKey,
  icon,
  badge,
  title,
  description,
  ctaLabel,
  onCta,
  secondaryLabel = 'Maybe later',
  delayMs = 600,
  accent = 'primary',
}: PagePromoPopupProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(storageKey) === '1') return;
    const t = window.setTimeout(() => setOpen(true), delayMs);
    return () => window.clearTimeout(t);
  }, [storageKey, delayMs]);

  const close = () => {
    if (typeof window !== 'undefined') sessionStorage.setItem(storageKey, '1');
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background/70 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="promo-popup-title"
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative w-full max-w-md rounded-xl border bg-card p-6 shadow-2xl animate-in zoom-in-95 duration-200',
          accent === 'amber' ? 'border-amber-500/40' : 'border-primary/40',
        )}
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        {badge && (
          <div
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold mb-3',
              accent === 'amber' ? 'bg-amber-500/15 text-amber-400' : 'bg-primary/15 text-primary',
            )}
          >
            {icon}
            {badge}
          </div>
        )}

        <h2 id="promo-popup-title" className="font-display text-xl sm:text-2xl font-bold text-foreground mb-2 leading-tight">
          {title}
        </h2>
        <div className="text-sm text-muted-foreground mb-5">{description}</div>

        <div className="flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="ghost" size="sm" onClick={close} className="sm:flex-1">
            {secondaryLabel}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (typeof window !== 'undefined') sessionStorage.setItem(storageKey, '1');
              setOpen(false);
              onCta();
            }}
            className="sm:flex-1 font-semibold"
          >
            {ctaLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}